import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AccessProvider } from "./context/AccessContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import SudoGate from "./components/SudoGate";
import OfflineBanner from "./components/OfflineBanner";
import { PrivateBetaGate } from "./components/PrivateBetaGate";
import SeoMetaController from "./components/SeoMetaController";
// import ActiveOperationPill from "./components/ActiveOperationPill";  // removed — no floating launchers platform-wide
import StaleSessionBanner from "./components/StaleSessionBanner";
import PickerDebugPanel from "./components/PickerDebugPanel";
import UniversalApplicationShell from "./components/UniversalApplicationShell";
// import CacheCheckBadge from "./components/admin/CacheCheckBadge";  // removed — no floating launchers platform-wide
import {
  canAccessRoute,
  resolveEffectiveRole,
  founderModeRole as resolverFounderModeRole,
} from "./utils/roleResolver";

// Shared pages
import Landing from "./pages/Landing";
import Home from "./pages/Home";
import HomeRouter from "./pages/HomeRouter";
import IntakeProbe from "./pages/IntakeProbe";
import HandoffContractOnly from "./pages/HandoffContractOnly";
import UploadLab from "./pages/UploadLab";
import LandingLearner from "./pages/LandingLearner";
import LandingPDI from "./pages/LandingPDI";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Pricing from "./pages/Pricing";
import LearnerPassport from "./pages/LearnerPassport";
import PassportShareView from "./pages/PassportShareView";
import PassportInbox from "./pages/PassportInbox";
import PassportPackView from "./pages/PassportPackView";
import BetaAdmin from "./pages/BetaAdmin";
import AdminLiveSmoke from "./pages/AdminLiveSmoke";
import MobileShellDemo from "./pages/MobileShellDemo";
import InstructorFreedomDashboard from "./pages/InstructorFreedomDashboard";
import PupilInvite from "./pages/PupilInvite";
import PupilBooking from "./pages/PupilBooking";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Learner pages
import LearnerHub from "./pages/LearnerHub";
import LearnerHubOverview from "./pages/LearnerHubOverview";
import LearnerLearningEnvironment from "./pages/LearnerLearningEnvironment";
import RoundaboutsHub from "./pages/RoundaboutsHub";
import JunctionsHub from "./pages/JunctionsHub";
import RoadPositioningHub from "./pages/RoadPositioningHub";
import TrainingMode from "./pages/TrainingMode";
import AIOfferAccept from "./pages/AIOfferAccept";
import ReviewIntentChooser from "./pages/ReviewIntentChooser";
import StageView from "./pages/StageView";
import LearnerMessages from "./pages/LearnerMessages";
import LearnerOnboarding from "./pages/LearnerOnboarding";
import HowILearn from "./pages/HowILearn";
import LearnerBooking from "./pages/LearnerBooking";
import LearnerLessons from "./pages/LearnerLessons";
import LearnerHomework from "./pages/LearnerHomework";
import LearnerProgress from "./pages/LearnerProgress";
import AIReviewRequests from "./pages/AIReviewRequests";
import ConnectInstructor from "./pages/ConnectInstructor";
import BookingSuccess from "./pages/BookingSuccess";
import RecurringBooking from "./pages/RecurringBooking";
import RecurringSuccess from "./pages/RecurringSuccess";
import WaitlistPage from "./pages/WaitlistPage";
import LearnerLastMinute from "./pages/LearnerLastMinute";
import LearnerUpgrade from "./pages/LearnerUpgrade";
import LearnerMoney from "./pages/LearnerMoney";
import InstructorWork from "./pages/InstructorWork";
import InstructorTraining from "./pages/InstructorTraining";
import InstructorProgress from "./pages/InstructorProgress";
import InstructorMoney from "./pages/InstructorMoney";
import InstructorMoneyConfirmations from "./pages/InstructorMoneyConfirmations";
import Dashboard from "./pages/Dashboard";
import Section from "./pages/Section";
import Lesson from "./pages/Lesson";
import Assessment from "./pages/Assessment";
// B0 Step 5 — `ConfidenceCheck` standalone surface retired. Any
// historical bookmark to /confidence-check/:moduleId now redirects
// to /dashboard. The page component file and backend schema remain
// in place for data preservation only.
import DailyCheckIn from "./pages/DailyCheckIn";

// ADI / Instructor pages
import InstructorDashboard from "./pages/InstructorDashboard";
import UniversalUpload from "./pages/UniversalUpload";
// FG-5 · Recording Sessions surfaces (GC-27 · flag retired under FG-5-A · GC-30).
import RecordingSessionsIndex from "./pages/RecordingSessionsIndex";
import RecordingSessionDetail from "./pages/RecordingSessionDetail";
import RecordingSessionCreate from "./pages/RecordingSessionCreate";
import MultiCameraIntake from "./pages/MultiCameraIntake";
import ImportTodaysRecordings from "./pages/ImportTodaysRecordings";
import "./pages/ImportTodaysRecordings.css";
import LegacyIntakeRedirect from "./pages/LegacyIntakeRedirect";
import "./pages/UniversalUpload.css";
// FounderCockpit is now mounted inside UniversalApplicationShell (Aug 2026);
// no App.js-level import required.
// import FounderCockpit from "./components/founder_cockpit/FounderCockpit";
// import FloatingNdgChip from "./components/floating_ndg/FloatingNdgChip";  // removed — no floating launchers platform-wide
import BookingsHub from "./pages/BookingsHub";
import MoreIndex from "./pages/MoreIndex";
import UploadHistory from "./pages/UploadHistory";
import ReceiptHistory from "./pages/ReceiptHistory";
import MyRecords from "./pages/MyRecords";
import RecordsByType from "./pages/RecordsByType";
import ContextWorkspace from "./pages/ContextWorkspace";
import TalkToNDG from "./pages/TalkToNDG";
import "./pages/TalkToNDG.css";
import FounderMorningDigest from "./pages/FounderMorningDigest";
import "./pages/FounderMorningDigest.css";
import FounderIntelligence from "./pages/FounderIntelligence";
import PassportProfessionalPrivate from "./pages/PassportProfessionalPrivate";
import PassportProfessionalPublic from "./pages/PassportProfessionalPublic";
import PassportProfessionalPublicSettings from "./pages/PassportProfessionalPublicSettings";
import OnTheMoveReflection from "./pages/OnTheMoveReflection";
import MasteryBuilder from "./pages/MasteryBuilder";
import MasterySkillDetail from "./pages/MasterySkillDetail";
import LicenceCheck from "./pages/LicenceCheck";
import LicenceInstructorPanel from "./pages/LicenceInstructorPanel";
import AccountantShareView from "./pages/AccountantShareView";
import PassportProfile from "./pages/PassportProfile";
import TransferReadyView from "./pages/TransferReadyView";
import InstructorPassport from "./pages/InstructorPassport";
import EnquiryIntake from "./pages/EnquiryIntake";
import InstructorNoShowAuditPage from "./pages/InstructorNoShowAuditPage";
import InstructorSchedulingPrefsPage from "./pages/InstructorSchedulingPrefsPage";
import TravelAwareMockupGallery from "./pages/_TravelAwareMockupGallery";
import InstructorBadgeUpload from "./pages/InstructorBadgeUpload";
import AdminBadgeQueue from "./pages/AdminBadgeQueue";
import AdminLearnerStories from "./pages/AdminLearnerStories";
import AdminEmailAudit from "./pages/AdminEmailAudit";
import AdminClientErrors from "./pages/AdminClientErrors";
import AdminNeedsAttention from "./pages/AdminNeedsAttention";
import AdminPatterns from "./pages/AdminPatterns";
import InstructorPatterns from "./pages/InstructorPatterns";
import Territory from "./pages/Territory";
import TrainingLocations from "./pages/TrainingLocations";
import LearnerScenario from "./pages/LearnerScenario";
import PdiScenario from "./pages/PdiScenario";
import MockPart3 from "./pages/MockPart3";
import InstructorWeeklyReviewPrefs from "./pages/InstructorWeeklyReviewPrefs";
import StandardsDashboard from "./pages/StandardsDashboard";
import CompliancePage from "./pages/CompliancePage";
import AdminContentLibrary from "./pages/AdminContentLibrary";
import NetworkBoard from "./pages/NetworkBoard";
import TransferInbox from "./pages/TransferInbox";
import CalendarPlanner from "./pages/CalendarV2Preview";
import PupilProfile from "./pages/PupilProfile";
import InstructorOverview from "./pages/InstructorOverview";
import WorkMode from "./pages/WorkMode";
import PDIHub from "./pages/PDIHub";
import SupervisorPortal, { SupervisorAccept } from "./pages/SupervisorPortal";
import TrainerPath from "./pages/TrainerPath";
import InstructorOnboarding from "./pages/InstructorOnboarding";
import UnderstandingOnboarding from "./pages/UnderstandingOnboarding";
import MigrationTools from "./pages/MigrationTools";
import SmsSettings from "./pages/SmsSettings";
import MarketplaceSearch, { MarketplaceInstructor } from "./pages/MarketplaceSearch";
import MarketplaceSettings from "./pages/MarketplaceSettings";
import MatchWizard from "./pages/MatchWizard";
import ShowTellDeck from "./pages/ShowTellDeck";
import HighwayCodeReader from "./pages/HighwayCodeReader";
import LessonReview from "./pages/LessonReview";
import OperationsHistory from "./pages/OperationsHistory";
import StandardsCheckReady from "./pages/StandardsCheckReady";
import CpdRecord from "./pages/CpdRecord";

// Admin pages
import AdminPanel from "./pages/AdminPanel";
import AdminHub from "./pages/AdminHub";
import AdminMediaBridge from "./pages/AdminMediaBridge";
import PreflightC2Page from "./pages/admin/PreflightC2Page";
import AdminMediaBridgeJobDetail from "./pages/AdminMediaBridgeJobDetail";
import AdminUnmatchedInstructorRequests from "./pages/AdminUnmatchedInstructorRequests";
import AdminUsers from "./pages/AdminUsers";
import AdminBookings from "./pages/AdminBookings";
import AdminReviews from "./pages/AdminReviews";
import QuarantinedRoute from "./components/QuarantinedRoute";
import AdminMoney from "./pages/AdminMoney";
import AdminRefundRetry from "./pages/AdminRefundRetry";
import AdminPaymentConfirmations from "./pages/AdminPaymentConfirmations";
import LessonPrepCard from "./pages/LessonPrepCard";
import Today from "./pages/Today";
import AutomationCentre from "./pages/AutomationCentre";
import AdminSystem from "./pages/AdminSystem";
import AdminContent from "./pages/AdminContent";
import AdminFunnels from "./pages/AdminFunnels";
import AdminStripeConnect from "./pages/AdminStripeConnect";
import AdminFunnel from "./pages/AdminFunnel";
// import BetaFeedbackFAB from "./components/beta/BetaFeedbackFAB";  // removed — no floating launchers platform-wide
// C3b · NPSPrompt removed (Article 4/7 — not attention-worthy).
import GroupSessions from "./pages/GroupSessions";
import CommunityLanding, { CommunitySpace, CommunityThread } from "./pages/Community";
import TransferNetworkSettings from "./pages/TransferNetworkSettings";
import ProgressTransfer from "./pages/ProgressTransfer";
import TransferOpportunities from "./pages/TransferOpportunities";
import TransferMatchChat from "./pages/TransferMatchChat";
import TransferHub from "./pages/TransferHub";
import GrowthLadder from "./pages/GrowthLadder";
import HelpCentre from "./pages/HelpCentre";
import AdminFounderMode from "./pages/AdminFounderMode";
import AdminPricingSettings from "./pages/AdminPricingSettings";
import AdminTeachingExcellence from "./pages/AdminTeachingExcellence";
import AdminCurriculumImport from "./pages/AdminCurriculumImport";
import LaunchSetup from "./pages/LaunchSetup";
import LearningHubV1 from "./pages/LearningHubV1";
import UploadShell from "./pages/UploadShell";
import IdentitySettings from "./pages/IdentitySettings";
import Settings from "./pages/Settings";
import ChatGPTAccountConnection from "./components/mail/ChatGPTAccountConnection";
import EmailConnectionsSettings from "./pages/EmailConnectionsSettings";
import NotificationPreferences from "./pages/NotificationPreferences";
import VerifyEmail from "./pages/VerifyEmail";
import FounderToday from "./pages/FounderToday";
// C3b · FounderModeBanner removed (Article 7 — chrome, not attention).
import TierSwitcher from "./components/dev/TierSwitcher";
import AdminRoleSwitcher from "./components/AdminRoleSwitcher";
import { LEGACY_PROGRESS_ENABLED } from "./featureFlags";
import { useEffect } from "react";

import "./App.css";

// ── Role-based route guard ──
// Founder-mode coherence helper — kept as a local alias for the
// central `resolverFounderModeRole` so existing call sites
// (HomeRedirect, PublicRoute) inside this file keep working
// without churn. The resolver is the single source of truth.
const founderModeRole = resolverFounderModeRole;

const homeForRole = (role) => {
  // C3a · 2026-08-07 — Constitutional Home is the single canonical
  // landing surface for every persona. Every legacy hub route
  // (see App.js `/dashboard`, `/instructor`, `/pdi-hub`, `/learn`,
  // `/more`, `/my`, `/today`) already redirects here, and the
  // `HomeRedirect` at `/` also targets `/home`. All programmatic
  // "back to home" redirects unify on the same surface.
  // (Supersedes GC63 · 2026-07-26 which returned /bookings.)
  void role;
  return "/home";
};

// Auth bootstrap failure panel (Feb 28, 2026).
// Operator brief: "add visible error handling instead of infinite
// 'Loading profile…' and log the exact failing endpoint/status."
// Used by both RoleRoute and ProtectedRoute when checkAuth() came
// back without a user AND captured a failure reason. Provides a
// human-readable explanation + Retry + Sign-out CTAs so the user
// is never left staring at a silent spinner that then yanks them
// to /login with no explanation.
const AuthBootstrapErrorPanel = ({ authError, onRetry }) => {
  const status = authError?.status ?? 0;
  const url = authError?.url || "/api/auth/me";
  const reason = authError?.reason || "unknown";
  const headline =
    reason === "unauthenticated"
      ? "Your session needs refreshing"
      : reason === "server_error"
      ? "Our server hit a temporary error"
      : reason === "network_or_cors"
      ? "We couldn't reach the server"
      : "Couldn't load your profile";
  const body =
    reason === "unauthenticated"
      ? "Please sign in again to continue."
      : reason === "server_error"
      ? "Please retry in a moment. If it keeps happening let us know."
      : reason === "network_or_cors"
      ? "Check your internet connection and try again."
      : "Try again — if it keeps happening, sign out and back in.";
  return (
    <div
      className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120] px-4"
      data-testid="auth-bootstrap-error-panel"
    >
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
        <h2 className="text-lg font-extrabold text-slate-900 dark:text-slate-100 mb-2">
          {headline}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">{body}</p>
        <div
          className="text-xs font-mono text-slate-500 dark:text-slate-400 mb-5 break-all"
          data-testid="auth-bootstrap-error-detail"
        >
          {url} → HTTP {status || "no-response"}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="auth-bootstrap-retry-btn"
            onClick={onRetry}
            className="flex-1 h-11 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
          >
            Retry
          </button>
          <a
            href="/login"
            data-testid="auth-bootstrap-signin-link"
            className="flex-1 h-11 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-semibold flex items-center justify-center"
          >
            Sign in
          </a>
        </div>
      </div>
    </div>
  );
};

const RoleRoute = ({ children, allowedRoles }) => {
  const { user, loading, authError, refreshUser } = useAuth();
  const location = useLocation();

  // Founder direction (2026-07-25): the C-Connector bulk-import
  // route must open directly — no /launch-setup wall, no admin
  // dashboard detour. Same principle for /talk (Universal Intent
  // front door). These are the only two ratified front doors, so
  // an admin with an incomplete launch-setup checklist can still
  // reach them and file evidence. All OTHER protected routes keep
  // the launch-setup gate exactly as before.
  const LAUNCH_SETUP_EXEMPT_PATHS = new Set([
    "/import-todays-recordings",
    "/talk",
    "/upload",
    "/bookings",
    "/more",
  ]);
  const isLaunchSetupExempt = LAUNCH_SETUP_EXEMPT_PATHS.has(location.pathname);

  if (loading) return (
    <div
      data-testid="auth-bootstrap-loading"
      className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]"
    >
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700" />
    </div>
  );

  // Surface an explicit error panel instead of an indefinite
  // spinner / silent redirect when the profile load actually
  // failed. The 401-interceptor will still take over for live
  // 401s; this branch is the fallback for first-mount races,
  // CORS, network, and 5xx.
  if (!user && authError) {
    return <AuthBootstrapErrorPanel authError={authError} onRetry={refreshUser} />;
  }

  if (!user) return <Navigate to="/login" replace />;

  // Trusted entry softens learner welcome friction, but instructor
  // accounts still need the role-specific setup once. This keeps
  // directed PDI signups operational without affecting pupil invites.

  // GC62 · 2026-07-25 — launch-setup is now a NUDGE inside /home for
  // admins, NOT a wall in front of every route. The Founder ratified:
  // "launch-setup is a nudge, not a wall." Removing the RoleRoute
  // redirect here restores admin access to /admin, /instructor,
  // /learn, /pdi-hub etc. even when the checklist is incomplete.
  // The /launch-setup page is still reachable directly from the /home
  // nudge; it is just no longer forced.
  //
  // isLaunchSetupExempt is retained for clarity but is now effectively
  // a no-op — kept in case a future rev wants to re-introduce a
  // scoped gate for a subset of routes without a big refactor.
  void isLaunchSetupExempt;

  // Effective role for onboarding gate + home-redirect target.
  // resolveEffectiveRole honors admin_view_role and operating_mode
  // (founder mode) so admin-in-preview is scoped to the previewed
  // persona, not bounced back to /admin.
  const effectiveRole = resolveEffectiveRole(user);

  // Gate instructors who haven't finished the wizard (except when
  // already on it, OR when they arrived via a trusted invite —
  // soft banner only. Also exempt the C-Connector / Universal
  // Intent front doors per Founder direction.)
  const isInstructor = ["adi", "pdi"].includes(effectiveRole);
  if (isInstructor && user.onboarding_complete === false && !isLaunchSetupExempt) {
    return <Navigate to="/onboarding/wizard" replace />;
  }

  // Central access decision — single source of truth shared with
  // every other persona-gating component (V3UploadEntryCard,
  // UploadShell mismatch branch, etc.).
  //
  // `allowAdminPassthrough: false` preserves legacy strict
  // behaviour: a plain admin (no preview persona) only passes when
  // "admin" is explicitly in `allowedRoles`. Admin in preview
  // persona is scoped to that persona's routes by definition.
  if (!canAccessRoute(allowedRoles, user, { allowAdminPassthrough: false })) {
    return <Navigate to={homeForRole(effectiveRole)} replace />;
  }

  return children;
};

// ── Protected route (any authenticated user) ──
const ProtectedRoute = ({ children }) => {
  const { user, loading, authError, refreshUser } = useAuth();
  if (loading) return <div data-testid="auth-bootstrap-loading" className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700" /></div>;
  if (!user && authError) {
    return <AuthBootstrapErrorPanel authError={authError} onRetry={refreshUser} />;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// ── Public route (redirect if logged in) ──
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700" /></div>;
  if (user) {
    // Trusted entry never suppresses ADI/PDI first-run setup.
    // Founder-mode coherence
    const fmRole = founderModeRole(user);
    const effectiveRole = fmRole
      ? fmRole
      : (user.role === "admin" ? (user.admin_view_role || "admin") : user.role);
    // Instructor onboarding wizard is preserved — that gate still fires.
    const isInstructor = ["adi", "pdi"].includes(effectiveRole);
    if (isInstructor && user.onboarding_complete === false) return <Navigate to="/onboarding/wizard" replace />;
    // GC62 · 2026-07-25 — every persona lands on /home. Launch-setup is
    // now a nudge/reminder inside /home, not a wall in front of it.
    return <Navigate to={homeForRole(effectiveRole)} replace />;
  }
  return children;
};

// ── Smart home redirect ──
const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700" /></div>;
  if (!user) return <Landing />;
  // Trusted entry never suppresses ADI/PDI first-run setup.
  const fmRole = founderModeRole(user);
  const effectiveRole = fmRole
    ? fmRole
    : (user.role === "admin" ? (user.admin_view_role || "admin") : user.role);
  const isInstructor = ["adi", "pdi"].includes(effectiveRole);
  if (isInstructor && user.onboarding_complete === false) return <Navigate to="/onboarding/wizard" replace />;
  // C3a · single canonical Home for every authed user.
  return <Navigate to="/home" replace />;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <UniversalApplicationShell sessionControls={<><OfflineBanner /><TierSwitcher /><AdminRoleSwitcher /></>}>
      <SeoMetaController />
      <StaleSessionBanner />
      {/* P0 (Feb 8, 2026 — second iteration) — global cross-page
          processing chip. When a user has a non-terminal lesson
          review pointer in localStorage, surface a recovery
          affordance from EVERY route, not just /lesson-review.
          Solves the production bug where iOS Safari "kicks the
          user away" post-100% and they have no path back.

          E3 (May 14, 2026 — Eagle-Eyed Operational Review §3):
          ProcessingReviewChip retired. The Active Operation pill
          (mobile global) now covers the same user-visible case
          via server-side SSOT (GET /api/active-operation/me) and
          the /operations history page covers retrospective
          recovery. Removed to eliminate duplicate-banner risk
          and Legacy Removal violation. */}
      {/* E3 (May 14, 2026): ActiveBasketChip retired together with
          ProcessingReviewChip — same Active Operation system now
          owns server-truth basket recovery. */}
      <Routes>
      {/* Public — wrapped in PrivateBetaGate so anonymous visitors see the
         launching-soon splash while PRIVATE_MODE is on. Members + admins +
         routes like /login, /register, /admin pass through. */}
      <Route path="/" element={<PrivateBetaGate><HomeRedirect /></PrivateBetaGate>} />
      <Route path="/for-learners" element={<PrivateBetaGate><LandingLearner /></PrivateBetaGate>} />
      <Route path="/for-pdis" element={<PrivateBetaGate><LandingPDI /></PrivateBetaGate>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/launch-setup" element={<ProtectedRoute><LaunchSetup /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Diagnostic — pure forensic file-input probe. Admin-only
          (was public; locked May 26, 2026 release-readiness pass). */}
      <Route path="/intake-probe" element={<RoleRoute allowedRoles={["admin"]}><IntakeProbe /></RoleRoute>} />
      <Route path="/handoff-contract-only" element={<RoleRoute allowedRoles={["admin"]}><HandoffContractOnly /></RoleRoute>} />
      {/* /upload-lab — canonical forensic upload environment.
          Auth-gated (any logged-in user). Role-agnostic per
          CANONICAL_UPLOAD_ARCHITECTURE.md §8 and
          PLATFORM_REBUILD_ARCHITECTURE.md §0. The page suppresses
          its own chrome; we MUST NOT add layout wrappers here. */}
      <Route path="/upload-lab" element={<ProtectedRoute><QuarantinedRoute area="Upload Lab" title="Upload Lab coming soon"><UploadLab /></QuarantinedRoute></ProtectedRoute>} />
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* CORRECTIVE PROGRAMME (2026-07-25) · GC60 One-Front-Door restoration. */}
      {/* Every historical Founder-facing intake surface below is now a graceful */}
      {/* redirect to the ratified /import-todays-recordings + /talk pair.       */}
      {/* Guarded by tests/architecture/test_one_front_door.py — CI fails if     */}
      {/* any additional user-facing intake path is added.                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* GC63 · Bookings Hub — the operational homepage (2026-07-26). */}
      <Route path="/bookings" element={<ProtectedRoute><BookingsHub /></ProtectedRoute>} />
      {/* C3a · duplicate hub route (MoreIndex) retired; redirect above. */}
      {/* GC64 Phase A · Upload History — the tappable "where did my uploads go?" answer. */}
      <Route path="/my/uploads" element={<ProtectedRoute><UploadHistory /></ProtectedRoute>} />
      {/* GC64 Phase B · My Records hub + generic per-type list. */}
      {/* C3a · duplicate hub route (MyRecords) retired; redirect above. */}
      <Route path="/my/:entityType" element={<ProtectedRoute><RecordsByType /></ProtectedRoute>} />
      {/* GC65 Phase C · Contextual workspace — one reusable page per entity. */}
      <Route path="/context/:entityType/:entityId" element={<ProtectedRoute><ContextWorkspace /></ProtectedRoute>} />
      {/* Legacy /home route retired — redirect to /bookings so bookmarks still work. */}
      {/* C3a · Home domain consolidation · single canonical Home. */}
      <Route path="/home" element={<ProtectedRoute><HomeRouter /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      <Route path="/instructor" element={<Navigate to="/home" replace />} />
      <Route path="/instructor-overview" element={<Navigate to="/home" replace />} />
      <Route path="/instructor/work" element={<Navigate to="/home" replace />} />
      <Route path="/instructor/freedom" element={<Navigate to="/home" replace />} />
      <Route path="/learn" element={<Navigate to="/home" replace />} />
      <Route path="/learn/overview" element={<Navigate to="/home" replace />} />
      <Route path="/pdi-hub" element={<Navigate to="/home" replace />} />
      <Route path="/more" element={<Navigate to="/home" replace />} />
      <Route path="/my" element={<Navigate to="/home" replace />} />
      <Route path="/today" element={<Navigate to="/home" replace />} />
      {/* /instructor/today remains — canonical Objective→Recommendation→Go workflow (Batches S1-S8). */}
      <Route path="/upload" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><UniversalUpload /></RoleRoute>} />
      <Route path="/learner/upload" element={<ProtectedRoute><LegacyIntakeRedirect from="/learner/upload" /></ProtectedRoute>} />
      <Route path="/instructor/upload" element={<ProtectedRoute><LegacyIntakeRedirect from="/instructor/upload" /></ProtectedRoute>} />
      <Route path="/pdi/upload" element={<ProtectedRoute><LegacyIntakeRedirect from="/pdi/upload" /></ProtectedRoute>} />
      <Route path="/recording-sessions" element={<ProtectedRoute><LegacyIntakeRedirect from="/recording-sessions" /></ProtectedRoute>} />
      <Route path="/recording-sessions/new" element={<ProtectedRoute><LegacyIntakeRedirect from="/recording-sessions/new" /></ProtectedRoute>} />
      <Route path="/recording-sessions/:id/multi-intake" element={<ProtectedRoute><LegacyIntakeRedirect from="/recording-sessions/:id/multi-intake" /></ProtectedRoute>} />
      <Route path="/recording-sessions/:id" element={<ProtectedRoute><LegacyIntakeRedirect from="/recording-sessions/:id" /></ProtectedRoute>} />
      {/* Universal Intent · UI-S3 Multi-Camera Intake (GC60 Phase A · Priority 2) — SOLE Founder evidence intake. */}
      <Route path="/import-todays-recordings" element={<RoleRoute allowedRoles={["learner","adi","pdi","admin"]}><ImportTodaysRecordings /></RoleRoute>} />
      {/* Universal Intent · Orchestrator (GC60 Phase A · Priority 3) — SOLE conversational front door. */}
      <Route path="/talk" element={<ProtectedRoute><TalkToNDG /></ProtectedRoute>} />
      {/* Founder Dashboard · Morning Digest (GC60 Phase A · Priority 4). */}
      <Route path="/founder/morning-digest" element={<RoleRoute allowedRoles={["adi","pdi","admin"]}><FounderMorningDigest /></RoleRoute>} />
      <Route path="/passport/professional" element={<RoleRoute allowedRoles={["adi", "pdi"]}><PassportProfessionalPrivate /></RoleRoute>} />
      <Route path="/passport/professional/public/settings" element={<RoleRoute allowedRoles={["adi", "pdi"]}><PassportProfessionalPublicSettings /></RoleRoute>} />
      <Route path="/passport/:publicSlug" element={<PassportProfessionalPublic />} />
      <Route path="/work-mode/reflect-on-the-move" element={<RoleRoute allowedRoles={["adi", "pdi"]}><OnTheMoveReflection /></RoleRoute>} />
      {/* Founder Intelligence Foundation · admin-only inventory
          over NDG Intelligence contributions. No scoring, no
          recommendations in V1 — a truthful ledger. */}
      <Route path="/founder/intelligence" element={<RoleRoute allowedRoles={["admin"]}><FounderIntelligence /></RoleRoute>} />
      <Route path="/pricing" element={<PrivateBetaGate><Pricing /></PrivateBetaGate>} />
      <Route path="/passport" element={<RoleRoute allowedRoles={["learner"]}><LearnerPassport /></RoleRoute>} />
      <Route path="/passport/profile" element={<RoleRoute allowedRoles={["learner"]}><PassportProfile /></RoleRoute>} />
      <Route path="/passport/transfer-view" element={<RoleRoute allowedRoles={["learner"]}><TransferReadyView /></RoleRoute>} />
      <Route path="/instructor/passport" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorPassport /></RoleRoute>} />
      <Route path="/instructor/enquiries" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><EnquiryIntake /></RoleRoute>} />
      <Route path="/admin/enquiries" element={<RoleRoute allowedRoles={["admin"]}><EnquiryIntake /></RoleRoute>} />
      <Route path="/instructor/no-show-audit" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorNoShowAuditPage /></RoleRoute>} />
      <Route path="/instructor/scheduling-prefs" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorSchedulingPrefsPage /></RoleRoute>} />
      {/* Static design-pass mockup gallery — Feb 18, 2026.
          Locked to admin (May 26, 2026 release-readiness pass). */}
      <Route path="/_mockup/travel-aware" element={<RoleRoute allowedRoles={["admin"]}><TravelAwareMockupGallery /></RoleRoute>} />
      <Route path="/instructor/verification" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorBadgeUpload /></RoleRoute>} />
      <Route path="/admin/badge-queue" element={<RoleRoute allowedRoles={["admin"]}><AdminBadgeQueue /></RoleRoute>} />
      <Route path="/admin/learner-stories" element={<RoleRoute allowedRoles={["admin"]}><AdminLearnerStories /></RoleRoute>} />
      <Route path="/admin/email-audit" element={<RoleRoute allowedRoles={["admin"]}><AdminEmailAudit /></RoleRoute>} />
      <Route path="/admin/client-errors" element={<RoleRoute allowedRoles={["admin"]}><AdminClientErrors /></RoleRoute>} />
      <Route path="/admin/needs-attention" element={<RoleRoute allowedRoles={["admin"]}><AdminNeedsAttention /></RoleRoute>} />
      <Route path="/admin/patterns" element={<RoleRoute allowedRoles={["admin"]}><AdminPatterns /></RoleRoute>} />
      <Route path="/instructor/patterns" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorPatterns /></RoleRoute>} />
      <Route path="/territory" element={<RoleRoute allowedRoles={["adi", "pdi", "instructor", "admin"]}><Territory /></RoleRoute>} />
      <Route path="/training-locations" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><TrainingLocations /></RoleRoute>} />
      <Route path="/admin/content-library" element={<RoleRoute allowedRoles={["admin"]}><AdminContentLibrary /></RoleRoute>} />
      <Route path="/learn-mode/scenario/:scenarioId" element={<RoleRoute allowedRoles={["learner", "admin"]}><LearnerScenario /></RoleRoute>} />
      <Route path="/pdi-mode/mock-part3" element={<RoleRoute allowedRoles={["pdi", "admin"]}><MockPart3 /></RoleRoute>} />
      <Route path="/pdi-mode/scenario/:scenarioId" element={<RoleRoute allowedRoles={["pdi", "admin"]}><PdiScenario /></RoleRoute>} />
      <Route path="/network" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><NetworkBoard /></RoleRoute>} />
      <Route path="/instructor/transfers" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><TransferInbox /></RoleRoute>} />
      <Route path="/p/share/:token" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><PassportShareView /></RoleRoute>} />
      <Route path="/pack/view/:token" element={<ProtectedRoute><PassportPackView /></ProtectedRoute>} />
      <Route path="/passport-inbox" element={<RoleRoute allowedRoles={["adi", "admin"]}><PassportInbox /></RoleRoute>} />
      <Route path="/admin/beta" element={<RoleRoute allowedRoles={["admin"]}><BetaAdmin /></RoleRoute>} />
      <Route path="/admin/live-smoke" element={<RoleRoute allowedRoles={["admin"]}><AdminLiveSmoke /></RoleRoute>} />
      <Route path="/test/mobile-shell-demo" element={<RoleRoute allowedRoles={["admin"]}><MobileShellDemo /></RoleRoute>} />
      {/* C3a · duplicate hub route (InstructorFreedomDashboard) retired; redirect above. */}
      <Route path="/instructor/mastery" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><MasteryBuilder /></RoleRoute>} />
      <Route path="/instructor/mastery/:skillId" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><MasterySkillDetail /></RoleRoute>} />
      <Route path="/instructor/licences" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><LicenceInstructorPanel /></RoleRoute>} />
      <Route path="/licence" element={<RoleRoute allowedRoles={["learner"]}><LicenceCheck /></RoleRoute>} />
      <Route path="/accountant/share/:token" element={<AccountantShareView />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/join/:referralCode" element={<Register />} />
      <Route path="/invite/:code" element={<PupilInvite />} />
      <Route path="/book/:code" element={<PupilBooking />} />
      <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />

      {/* Operational Continuity Phase D (May 14, 2026) — calm
          chronological history of the user's recent operations. All
          authenticated roles. */}
      <Route path="/operations" element={<ProtectedRoute><OperationsHistory /></ProtectedRoute>} />

      {/* ═══ LEARNER ROUTES ═══ */}
      {/* C3a · duplicate hub route (LearnerHub) retired; redirect above. */}
      <Route path="/learn/overview" element={<RoleRoute allowedRoles={["learner"]}><LearnerHubOverview /></RoleRoute>} />
      <Route path="/learn/learning" element={<RoleRoute allowedRoles={["learner"]}><LearnerLearningEnvironment /></RoleRoute>} />
      <Route path="/learn/hub" element={<RoleRoute allowedRoles={["learner", "admin"]}><LearningHubV1 /></RoleRoute>} />
      <Route path="/learn/hub/:slug" element={<RoleRoute allowedRoles={["learner", "admin"]}><LearningHubV1 /></RoleRoute>} />
      {/* V3 media controller · SUNSET 2026-07-25 (GC60 One-Front-Door
          restoration). These three persona routes are collapsed into the
          single Founder-facing evidence intake path /import-todays-recordings.
          Kept here only as graceful legacy redirects; will be removed once
          the deployed build and downstream bookmarks have drained. */}
      <Route path="/learn/homework" element={<RoleRoute allowedRoles={["learner"]}><LearnerHomework /></RoleRoute>} />
      <Route path="/learn/ai-review-requests" element={<RoleRoute allowedRoles={["learner"]}><QuarantinedRoute area="AI review requests" title="AI review requests coming soon"><AIReviewRequests /></QuarantinedRoute></RoleRoute>} />
      <Route path="/learn/connect" element={<RoleRoute allowedRoles={["learner"]}><ConnectInstructor /></RoleRoute>} />
      <Route path="/roundabouts" element={<RoleRoute allowedRoles={["learner", "admin"]}><RoundaboutsHub /></RoleRoute>} />
      <Route path="/junctions" element={<RoleRoute allowedRoles={["learner", "admin"]}><JunctionsHub /></RoleRoute>} />
      <Route path="/road-positioning" element={<RoleRoute allowedRoles={["learner", "admin"]}><RoadPositioningHub /></RoleRoute>} />
      <Route path="/training" element={<RoleRoute allowedRoles={["learner", "admin"]}><TrainingMode /></RoleRoute>} />
      <Route path="/ai-offer-accept" element={<RoleRoute allowedRoles={["learner"]}><QuarantinedRoute area="AI review offers" title="AI review offers coming soon"><AIOfferAccept /></QuarantinedRoute></RoleRoute>} />
      <Route path="/start-review" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><QuarantinedRoute area="Start a review" title="Start review coming soon"><ReviewIntentChooser /></QuarantinedRoute></RoleRoute>} />
      <Route path="/stage/:slug" element={<RoleRoute allowedRoles={["learner", "admin"]}><StageView /></RoleRoute>} />
      <Route path="/learn/messages" element={<RoleRoute allowedRoles={["learner"]}><LearnerMessages /></RoleRoute>} />
      <Route path="/learn/messages/:threadId" element={<RoleRoute allowedRoles={["learner"]}><LearnerMessages /></RoleRoute>} />
      <Route path="/learn/styles" element={<RoleRoute allowedRoles={["learner"]}><HowILearn /></RoleRoute>} />
      <Route path="/learn/styles/:styleId" element={<RoleRoute allowedRoles={["learner"]}><HowILearn /></RoleRoute>} />
      {/* Phase Pl-C (Feb 7, 2026) — single bundled lessons hub.
          Old /learn/book* routes stay alive; this is purely a router. */}
      <Route path="/learn/lessons" element={<RoleRoute allowedRoles={["learner"]}><LearnerLessons /></RoleRoute>} />
      <Route path="/learn/book" element={<RoleRoute allowedRoles={["learner"]}><LearnerBooking /></RoleRoute>} />
      <Route path="/learn/book/success" element={<RoleRoute allowedRoles={["learner"]}><BookingSuccess /></RoleRoute>} />
      <Route path="/learn/book/recurring" element={<RoleRoute allowedRoles={["learner"]}><RecurringBooking /></RoleRoute>} />
      <Route path="/learn/book/recurring/success" element={<RoleRoute allowedRoles={["learner"]}><RecurringSuccess /></RoleRoute>} />
      <Route path="/learn/waitlist" element={<RoleRoute allowedRoles={["learner"]}><WaitlistPage /></RoleRoute>} />
      <Route path="/learn/last-minute" element={<RoleRoute allowedRoles={["learner", "pdi", "admin"]}><LearnerLastMinute /></RoleRoute>} />
      {/* Phase 1 (Feb 6, 2026) — single-purpose Money / Upgrade pages.
          Learner-only; PDI/ADI/Admin nav untouched. */}
      <Route path="/upgrade" element={<RoleRoute allowedRoles={["learner"]}><LearnerUpgrade /></RoleRoute>} />
      <Route path="/money" element={<RoleRoute allowedRoles={["learner"]}><LearnerMoney /></RoleRoute>} />
      <Route path="/learn/progress" element={<RoleRoute allowedRoles={["learner"]}><LearnerProgress /></RoleRoute>} />
      {/* Phase Pl-E (Feb 7, 2026) — old dense module dashboard preserved
          at /learn/progress-legacy for anyone who wants granular detail.
          May 2026 (§23 Step 3 — Learner State Engine sunset sequence):
          gated on REACT_APP_ENABLE_LEGACY_PROGRESS. Default = ENABLED;
          flipping the flag to "0" redirects to /learn/progress without
          deleting Dashboard.js (full sunset is §23 Step 16). */}
      <Route
        path="/learn/progress-legacy"
        element={
          LEGACY_PROGRESS_ENABLED ? (
            <RoleRoute allowedRoles={["learner"]}><Dashboard /></RoleRoute>
          ) : (
            <Navigate to="/learn/progress" replace />
          )
        }
      />
      <Route path="/onboarding" element={<ProtectedRoute><LearnerOnboarding /></ProtectedRoute>} />
      {/* V1 Understanding Onboarding — role-aware starting-point capture
          (Feb 2026). Available to learner / pdi / adi. Reachable
          post-signup and again from the profile for edits. */}
      <Route path="/onboarding/understanding" element={<ProtectedRoute><UnderstandingOnboarding /></ProtectedRoute>} />
      <Route path="/daily-checkin" element={<RoleRoute allowedRoles={["learner"]}><DailyCheckIn /></RoleRoute>} />
      {/* C3a · duplicate hub route (Dashboard) retired; redirect above. */}
      <Route path="/section/:sectionSlug" element={<RoleRoute allowedRoles={["learner", "admin"]}><Section /></RoleRoute>} />
      <Route path="/lesson/:lessonId" element={<RoleRoute allowedRoles={["learner", "admin"]}><Lesson /></RoleRoute>} />
      <Route path="/assessment" element={<RoleRoute allowedRoles={["learner"]}><Assessment /></RoleRoute>} />
      <Route path="/confidence-check/:moduleId" element={<Navigate to="/dashboard" replace />} />

      {/* ═══ ADI + PDI (INSTRUCTOR OPERATIONAL) ROUTES — PDI mirrors ADI toolset ═══ */}
      {/* C3a · duplicate hub route (InstructorDashboard) retired; redirect above. */}
      {/* Phase 11.2 (Feb 6, 2026) — unified instructor tabs. */}
      {/* C3a · duplicate hub route (InstructorWork) retired; redirect above. */}
      <Route path="/instructor/training" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><QuarantinedRoute area="Instructor training & reviews" title="Instructor training coming soon"><InstructorTraining /></QuarantinedRoute></RoleRoute>} />
      <Route path="/instructor/progress" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorProgress /></RoleRoute>} />
      <Route path="/instructor/money" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorMoney /></RoleRoute>} />
      <Route path="/receipts" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><ReceiptHistory /></RoleRoute>} />
      <Route path="/instructor/money/confirmations" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorMoneyConfirmations /></RoleRoute>} />
      <Route path="/calendar-planner" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><CalendarPlanner /></RoleRoute>} />
      <Route path="/pupil/:pupilId" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><PupilProfile /></RoleRoute>} />
      <Route path="/instructor-overview" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><InstructorOverview /></RoleRoute>} />
      <Route path="/pupils" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><WorkMode pupilDirectory /></RoleRoute>} />
      <Route path="/work-mode" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><WorkMode /></RoleRoute>} />
      <Route path="/instructor/work-mode" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><WorkMode /></RoleRoute>} />
      <Route path="/instructor/weekly-review-prefs" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><QuarantinedRoute area="Weekly review preferences" title="Weekly review prefs coming soon"><InstructorWeeklyReviewPrefs /></QuarantinedRoute></RoleRoute>} />
      <Route path="/instructor/standards" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><StandardsDashboard /></RoleRoute>} />
      <Route path="/instructor/compliance" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><CompliancePage /></RoleRoute>} />

      {/* ═══ GROUP SESSIONS — open to all authenticated roles ═══ */}
      <Route path="/group-sessions" element={<ProtectedRoute><GroupSessions /></ProtectedRoute>} />

      {/* ═══ COMMUNITY SPACES — role-separated moderated forums ═══ */}
      <Route path="/community" element={<ProtectedRoute><CommunityLanding /></ProtectedRoute>} />
      <Route path="/community/:space" element={<ProtectedRoute><CommunitySpace /></ProtectedRoute>} />
      <Route path="/community/:space/threads/:threadId" element={<ProtectedRoute><CommunityThread /></ProtectedRoute>} />

      {/* ═══ PDI TRAINING LAYER ═══ */}
      {/* C3a · duplicate hub route (PDIHub) retired; redirect above. */}

      {/* ═══ OPTIONAL SUPERVISOR PORTAL — ADI doesn't need to migrate their business ═══ */}
      <Route path="/supervisor" element={<RoleRoute allowedRoles={["adi", "pdi", "admin", "learner"]}><SupervisorPortal /></RoleRoute>} />
      <Route path="/supervisor/accept" element={<SupervisorAccept />} />

      {/* ═══ ADI GROWTH: Become a PDI Trainer ═══ */}
      <Route path="/trainer-path" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><TrainerPath /></RoleRoute>} />

      {/* ═══ Fast Onboarding Wizard (ADI/PDI branching) ═══ */}
      <Route path="/onboarding/wizard" element={<ProtectedRoute><InstructorOnboarding /></ProtectedRoute>} />

      {/* ═══ Migration Tools — CSV import for pupils / bookings ═══ */}
      <Route path="/migration" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><MigrationTools /></RoleRoute>} />

      {/* ═══ SMS Settings — Twilio triggers + templates ═══ */}
      <Route path="/settings" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><Settings /></RoleRoute>} />
      <Route path="/settings/notifications" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><NotificationPreferences /></RoleRoute>} />
      <Route path="/settings/sms" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><SmsSettings /></RoleRoute>} />
      <Route path="/settings/chatgpt" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><ChatGPTAccountConnection /></RoleRoute>} />
      <Route path="/settings/email-connections" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><EmailConnectionsSettings /></RoleRoute>} />

      {/* ═══ Instructor Marketplace ═══ */}
      <Route path="/marketplace" element={<PrivateBetaGate><MarketplaceSearch /></PrivateBetaGate>} />
      <Route path="/marketplace/match" element={<PrivateBetaGate><MatchWizard /></PrivateBetaGate>} />
      <Route path="/learn/show-tell" element={<RoleRoute allowedRoles={["learner"]}><ShowTellDeck /></RoleRoute>} />
      <Route path="/learn/highway-code" element={<HighwayCodeReader />} />
      {/* Phase 4 / Phase F (Feb 13, 2026) — pupils can now read
          their own AI review (operator brief: pupil sees the
          instructor-confirmation pill INSIDE the review). The
          LessonReview component already gates which doc loads via
          `id` + auth — no extra protection needed at the route. */}
      <Route path="/lesson-review" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><QuarantinedRoute area="Lesson review" title="Lesson review coming soon"><LessonReview /></QuarantinedRoute></RoleRoute>} />
      <Route path="/standards-check-ready" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><StandardsCheckReady /></RoleRoute>} />
      <Route path="/cpd" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><CpdRecord /></RoleRoute>} />
      <Route path="/marketplace/instructor/:id" element={<MarketplaceInstructor />} />
      <Route path="/settings/marketplace" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><MarketplaceSettings /></RoleRoute>} />

      {/* ═══ ADMIN ROUTES ═══ */}
      {/* Feb 6, 2026 admin restructure — /admin is the new 6-card
          Command Centre hub. The legacy 1.8k-line AdminPanel still
          works at /admin/legacy as a safety net for any sub-flow not
          yet migrated into the new section pages. */}
      <Route path="/admin" element={<RoleRoute allowedRoles={["admin"]}><AdminHub /></RoleRoute>} />
      {/* Temporary LIVE C2 preflight page (Aug 2026) — deletable
          with backend routes/preflight_c2.py when C2 closes. */}
      <Route path="/admin/preflight/c2" element={<RoleRoute allowedRoles={["admin"]}><PreflightC2Page /></RoleRoute>} />
      <Route path="/admin/media-bridge" element={<RoleRoute allowedRoles={["admin"]}><AdminMediaBridge /></RoleRoute>} />
      <Route path="/admin/media-bridge/job/:jobId" element={<RoleRoute allowedRoles={["admin"]}><AdminMediaBridgeJobDetail /></RoleRoute>} />
      <Route path="/admin/unmatched-instructor-requests" element={<RoleRoute allowedRoles={["admin"]}><AdminUnmatchedInstructorRequests /></RoleRoute>} />
      <Route path="/admin/legacy" element={<RoleRoute allowedRoles={["admin"]}><AdminPanel /></RoleRoute>} />
      <Route path="/admin/users" element={<RoleRoute allowedRoles={["admin"]}><AdminUsers /></RoleRoute>} />
      <Route path="/admin/bookings" element={<RoleRoute allowedRoles={["admin"]}><AdminBookings /></RoleRoute>} />
      <Route path="/admin/reviews" element={<RoleRoute allowedRoles={["admin"]}><AdminReviews /></RoleRoute>} />
      <Route path="/admin/money" element={<RoleRoute allowedRoles={["admin"]}><AdminMoney /></RoleRoute>} />
      <Route path="/admin/refund-retry" element={<RoleRoute allowedRoles={["admin"]}><AdminRefundRetry /></RoleRoute>} />
      <Route path="/admin/payment-confirmations" element={<RoleRoute allowedRoles={["admin"]}><AdminPaymentConfirmations /></RoleRoute>} />
      <Route path="/instructor/prep/:lessonId" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><LessonPrepCard /></RoleRoute>} />
      <Route path="/instructor/today" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><Today /></RoleRoute>} />
      <Route path="/instructor/automation" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><AutomationCentre /></RoleRoute>} />
      <Route path="/admin/system" element={<RoleRoute allowedRoles={["admin"]}><AdminSystem /></RoleRoute>} />
      <Route path="/admin/content" element={<RoleRoute allowedRoles={["admin"]}><AdminContent /></RoleRoute>} />
      <Route path="/admin/funnels" element={<RoleRoute allowedRoles={["admin"]}><AdminFunnels /></RoleRoute>} />
      <Route path="/admin/stripe-connect" element={<RoleRoute allowedRoles={["admin"]}><AdminStripeConnect /></RoleRoute>} />
      <Route path="/admin/funnel" element={<RoleRoute allowedRoles={["admin"]}><AdminFunnel /></RoleRoute>} />
      <Route path="/admin/founder-mode" element={<RoleRoute allowedRoles={["admin"]}><AdminFounderMode /></RoleRoute>} />
      <Route path="/admin/pricing" element={<RoleRoute allowedRoles={["admin"]}><AdminPricingSettings /></RoleRoute>} />
      <Route path="/admin/teaching-excellence" element={<RoleRoute allowedRoles={["admin"]}><AdminTeachingExcellence /></RoleRoute>} />
      <Route path="/admin/curriculum" element={<RoleRoute allowedRoles={["admin"]}><AdminCurriculumImport /></RoleRoute>} />
      <Route path="/settings/identity" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><IdentitySettings /></RoleRoute>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      {/* C3a · duplicate hub route (FounderToday) retired; redirect above. */}
      <Route path="/settings/transfer-network" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><TransferNetworkSettings /></RoleRoute>} />
      <Route path="/instructor/transfer-opportunities" element={<RoleRoute allowedRoles={["adi", "pdi", "admin"]}><TransferOpportunities /></RoleRoute>} />
      <Route path="/learn/progress-transfer" element={<RoleRoute allowedRoles={["learner", "admin"]}><ProgressTransfer /></RoleRoute>} />
      <Route path="/learn/transfer-chat/:chatId" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><TransferMatchChat /></RoleRoute>} />
      <Route path="/learn/transfer-hub/:requestId" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><TransferHub /></RoleRoute>} />
      <Route path="/settings/growth-ladder" element={<RoleRoute allowedRoles={["learner", "adi", "pdi", "admin"]}><GrowthLadder /></RoleRoute>} />

      {/* ═══ HELP CENTRE — role-based user manuals + camera/upload guide ═══ */}
      <Route path="/help" element={<HelpCentre />} />
      <Route path="/help/:manualId" element={<HelpCentre />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {/* GC61 · Founder Cockpit — the canonical docked mobile primary
     *   navigation is now owned by UniversalApplicationShell (see
     *   `components/UniversalApplicationShell.jsx`). The previous
     *   floating-pill mount here was retired 7 Aug 2026 alongside
     *   the doctrine pins in `test_mobile_navigation_docked_doctrine.py`.
     *   No floating launchers, no pills, no FABs — anywhere.
     */}
    {/* GC63 · FloatingNdgChip removed platform-wide (Aug 2026)
     *   — no floating launchers anywhere in NDG. */}
    </UniversalApplicationShell>
  );
}

function App() {
  // GC62 · Dark-mode restoration. Read persisted preference and mirror to
  // <html data-theme=...>. Defaults to dark (design guidelines mandate
  // dark-first for ratified Universal Intent surfaces).
  useEffect(() => {
    try {
      const saved = localStorage.getItem("ndg-theme");
      const theme = (saved === "light" || saved === "dark") ? saved : "dark";
      document.documentElement.setAttribute("data-theme", theme);
    } catch {
      document.documentElement.setAttribute("data-theme", "dark");
    }
  }, []);

  // E3 (May 14, 2026 — Eagle-Eyed Operational Review §3):
  // the multi-clip recovery purge effect previously here has been
  // removed together with the legacy recovery surfaces. The purge
  // was a one-shot migration safety net that ran on every mount
  // since iter_186 — every existing user has long since had stale
  // records purged. The Active Operation system + /operations
  // history are now the canonical recovery layer.

  // Hide the "Made with Emergent" badge on mobile. The badge uses inline
  // `!important` styles which CSS cannot override, so we must use JS —
  // setProperty("display", "none", "important") beats inline !important.
  // Polled because the badge may be injected after mount.
  useEffect(() => {
    const hideBadgeOnMobile = () => {
      const el = document.getElementById("emergent-badge");
      if (!el) return;
      if (window.innerWidth <= 768) {
        el.style.setProperty("display", "none", "important");
        el.style.setProperty("visibility", "hidden", "important");
      } else {
        el.style.removeProperty("display");
        el.style.removeProperty("visibility");
      }
    };
    hideBadgeOnMobile();
    const interval = setInterval(hideBadgeOnMobile, 2000);
    window.addEventListener("resize", hideBadgeOnMobile);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", hideBadgeOnMobile);
    };
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AccessProvider>
            {/* Session/status strips participate in AppRoutes' shell height. */}
            <AppRoutes />
            <SudoGate />
            {/* Global floating launchers removed per platform-wide
             *  UI correction (Aug 2026). Nothing may float above or
             *  overlap the working surface anywhere in NDG. Both
             *  BetaFeedbackFAB and ActiveOperationPill are now
             *  suppressed — any surfacing they did must be relocated
             *  inline into the relevant page layout. */}
            {/* C3b · <NPSPrompt /> removed. */}
            <PickerDebugPanel />
            {/* CacheCheckBadge removed — no floating launchers platform-wide */}
            <Toaster position="top-center" richColors />
          </AccessProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;


